import { Database } from '@/types/database.types';
import { createClient } from '@supabase/supabase-js';
import { getRootDomain } from './cookies';

// สร้าง Supabase client
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

// Get the appropriate cookie domain from centralized utility
const cookieDomain = getRootDomain();
// console.log('[Supabase] Using cookie domain:', cookieDomain);

// Create a custom storage implementation for cross-domain support
const createCrossDomainStorage = () => {
  // console.log('[Auth Storage] Initializing cross-domain storage with domain:', cookieDomain);
  
  // Log all existing cookies
  // console.log('[Auth Storage] Current cookies on initialization:', document.cookie);
  // console.log('[Auth Storage] Current hostname:', window.location.hostname);
  
  return {
    getItem: (key: string) => {
      // console.log(`[Auth Storage] getItem called for key: "${key}"`);
      
      try {
        // First try localStorage
        const localValue = localStorage.getItem(key);
        // console.log(`[Auth Storage] localStorage value for "${key}":`, localValue ? `Found (${localValue.slice(0, 20)}...)` : 'Not found');
        
        // Also check if exists in cookies for debugging
        const cookies = document.cookie.split(';').map(c => c.trim());
        const cookieExists = cookies.some(c => c.startsWith(`${key}=`));
        // console.log(`[Auth Storage] Cookie exists for "${key}":`, cookieExists);
        // console.log(`[Auth Storage] All cookies:`, document.cookie);
        
        return localValue;
      } catch (error) {
        console.error(`[Auth Storage] Error in getItem("${key}"):`, error);
        return null;
      }
    },
    
    setItem: (key: string, value: string) => {
      // console.log(`[Auth Storage] setItem called for key: "${key}"`);
      // console.log(`[Auth Storage] Value type:`, typeof value);
      // console.log(`[Auth Storage] Value length:`, value?.length);
      // console.log(`[Auth Storage] Value preview:`, value?.slice(0, 50) + '...');
      
      try {
        // Store in localStorage
        localStorage.setItem(key, value);
        // console.log(`[Auth Storage] Successfully saved to localStorage: "${key}"`);
        
        // Also set a cookie at the root domain
        if (key.includes('auth') || key.includes('supabase')) {
          // Set max age to 7 days
          const maxAge = 60 * 60 * 24 * 7;
          
          // Create cookie string
          const cookieStr = `${key}=${encodeURIComponent(value)};domain=${cookieDomain};path=/;max-age=${maxAge};samesite=lax;${window.location.protocol === 'https:' ? 'secure;' : ''}`;
          
          // Log cookie details before setting
          console.log(`[Auth Storage] Setting cookie with domain "${cookieDomain}"`);
          console.log(`[Auth Storage] Cookie string:`, cookieStr.slice(0, 100) + '...');
          
          // Set the cookie
          document.cookie = cookieStr;
          
          // Verify cookie was set
          const cookieExists = document.cookie.includes(key + '=');
          // console.log(`[Auth Storage] Cookie was set: ${cookieExists}`);
          // console.log(`[Auth Storage] All cookies after setting:`, document.cookie.split(';').map(c => c.trim().split('=')[0]));
        }
      } catch (error) {
        console.error(`[Auth Storage] Error in setItem("${key}"):`, error);
      }
    },
    
    removeItem: (key: string) => {
      // console.log(`[Auth Storage] removeItem called for key: "${key}"`);
      
      try {
        // Remove from localStorage
        localStorage.removeItem(key);
        // console.log(`[Auth Storage] Removed from localStorage: "${key}"`);
        
        // Also remove the cookie if it exists
        const cookieStr = `${key}=;domain=${cookieDomain};path=/;max-age=0;${window.location.protocol === 'https:' ? 'secure;' : ''}`;
        document.cookie = cookieStr;
        // console.log(`[Auth Storage] Removed cookie with string: ${cookieStr}`);
        
        // Verify removal
        const stillExists = document.cookie.includes(key + '=');
        // console.log(`[Auth Storage] Cookie still exists after removal: ${stillExists}`);
      } catch (error) {
        console.error(`[Auth Storage] Error in removeItem("${key}"):`, error);
      }
    }
  };
};

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
    // Use the cross-domain storage implementation
    storage: createCrossDomainStorage(),
  }
});
